/**
 * What the research map's views compute apart from how they draw it: the gap order, the snapshot label, the
 * tombstone of a missing or moved source, a record's name and material. Plain .ts, free of JSX, so it is tested
 * on literals under `node --test`.
 */
import type { CheckedRef, GapClassification, ResearchBlocked, ResearchCapabilities, ResearchMapGap, ResearchRecord, SourceStatus } from "../../../shared/wire/research.ts";
import { DOMAIN_LABELS, SOURCE_NOTICE_LABEL, timeLabel } from "./researchLabels.ts";

const CLASS_ORDER: Record<GapClassification, number> = { user_confirmed: 0, agent_inferred: 1, not_recorded: 2 };
const DERIVATION_ORDER: Record<ResearchMapGap["derivation"], number> = { record: 0, rule: 1, coverage: 2 };

/** Confirmed gaps first, then grounded proposals (declared before rule-derived), then what is not recorded. */
export function orderGaps(gaps: readonly ResearchMapGap[]): ResearchMapGap[] {
	return [...gaps].sort(
		(a, b) =>
			CLASS_ORDER[a.classification] - CLASS_ORDER[b.classification] ||
			DERIVATION_ORDER[a.derivation] - DERIVATION_ORDER[b.derivation] ||
			(a.createdAt ?? 0) - (b.createdAt ?? 0) ||
			a.id.localeCompare(b.id),
	);
}

/** What a map's footer says of its time: the transcript's snapshot until a refresh replaces it. */
export function snapshotLabel(snapshotAt: number, refreshedAt: number | null): string {
	return refreshedAt ? `已刷新 · ${timeLabel(refreshedAt)}` : `快照 · ${timeLabel(snapshotAt)}`;
}

/** A missing or moved source: its tombstone, with the snapshot that outlived it. */
export function tombstone(ref: CheckedRef): { label: string; path: string; artifactId: string | null; movedTo: string | null } | null {
	if (ref.sourceStatus !== "missing" && ref.sourceStatus !== "moved") return null;
	return { label: SOURCE_NOTICE_LABEL[ref.sourceStatus], path: typeof ref.path === "string" ? ref.path : "", artifactId: ref.artifactId ?? null, movedTo: ref.movedTo ?? null };
}

const SEVERITY: SourceStatus[] = ["missing", "moved", "ambiguous", "unlocated", "stale", "unretrieved", "current"];

/** The status that needs a look first among a record's sources. */
export function worstSourceStatus(refs: readonly CheckedRef[]): SourceStatus | null {
	for (const status of SEVERITY) if (refs.some((r) => r.sourceStatus === status)) return status;
	return null;
}

/** What a blocked view is headed: project selection, or a domain this server does not deploy. */
export function blockedCopy(blocked: ResearchBlocked): string {
	return blocked.code === "scope_required" ? "选择项目" : `${blocked.domain ? DOMAIN_LABELS[blocked.domain] : "该领域"}未部署`;
}

const LABEL_MAX = 120;

function str(value: unknown): string | undefined {
	return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function pathOf(value: unknown): string | undefined {
	return value && typeof value === "object" ? str((value as { path?: unknown }).path) : undefined;
}

/** What a record is called on the map: its title, text or statement, else its file's name, else its kind and id. */
export function recordLabel(record: Pick<ResearchRecord, "id" | "kind" | "body">): string {
	const b = record.body;
	const file = pathOf(b.fileRef) ?? pathOf(b.originalRef);
	const label = str(b.title) ?? str(b.text) ?? str(b.statement) ?? str(b.name) ?? str(b.quote) ?? file?.split("/").pop() ?? `${record.kind} ${record.id.slice(0, 8)}`;
	return label.length > LABEL_MAX ? `${label.slice(0, LABEL_MAX - 1)}…` : label;
}

function isRef(value: unknown): value is CheckedRef {
	return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isMaterial(ref: CheckedRef): boolean {
	return Boolean(str(ref.path) ?? str(ref.url) ?? str(ref.citekey) ?? str(ref.handle));
}

/** Every material reference a record carries: its refs, its body's fileRef, originalRef and refs, one per path or identifier. */
export function materialRefs(record: Pick<ResearchRecord, "refs" | "body">): CheckedRef[] {
	const b = record.body;
	const all = [...record.refs, b.fileRef, b.originalRef, ...(Array.isArray(b.refs) ? b.refs : [])].filter(isRef).filter(isMaterial);
	const seen = new Set<string>();
	return all.filter((ref) => {
		const key = str(ref.path) ?? str(ref.url) ?? str(ref.citekey) ?? str(ref.handle) ?? JSON.stringify(ref);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

/** The domains a view's capabilities say are unavailable, as the footer names them. */
export function unavailableDomains(capabilities: ResearchCapabilities | null | undefined): string[] {
	if (!capabilities) return [];
	return (Object.keys(capabilities.domains) as Array<keyof typeof capabilities.domains>).filter((d) => !capabilities.domains[d].available).map((d) => DOMAIN_LABELS[d]);
}
